import React, { Fragment, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import compose from 'lodash.flowright';

import { STEPS } from './constants';
import SignupAlbumLayout from '../../../components/global/SignupAlbumLayout';
import { isValidPhone } from '../../../util/phone';
import UserInformationStep from './components/UserInformationStep';
import useMarketingAccountMatches from './hooks/useMarketingAccountMatches';
import FullScreenLoader from '../../../components/global/FullScreenLoader';
import PinVerificationStep from './components/PinVerificationStep';
import EmailVerificationStep from './components/EmailVerificationStep';
import useAuthenticated from '../../../hooks/useAuthenticated';
import EmailRegistrationStep from './components/EmailRegistrationStep/EmailRegistrationStep';
import EmailVerificationSentStep from './components/EmailVerificationSentStep';
import withGoogleRecaptchaProvider from '../../../components/auth/hocs/withGoogleRecaptchaProvider';

const MarketingAccountMatchesView = () => {
  const location = useLocation();
  const authenticated = useAuthenticated();
  const {
    firstName,
    lastName,
    accountId: marketingAccountId,
    emailAddress,
    albumId,
  } = qs.parse(location.search ?? '');
  const {
    step,
    setStep,
    loading,
    onPhoneVerificationRequest,
    onEmailRegistrationRequest,
    onEmailVerificationRequest,
    onPinVerify,
    onPinCodeResend,
    onSignIn,
    showEmailVerificationStep,
    checkingEmailVerificationStepRequirements,
    requireHumanInput,
    blockLogin,
  } = useMarketingAccountMatches({
    albumId,
    marketingAccountId,
    sourceEmailAddress: emailAddress,
  });

  // We will only do this on initial render since we need to avoid the case where
  // authenticating during the checkout results in redirecting wrongly.
  useEffect(() => {
    if (authenticated && !checkingEmailVerificationStepRequirements) {
      onSignIn();
    }
  }, [checkingEmailVerificationStepRequirements]);

  const onSubmit = values => {
    if (step === STEPS.USER_INFORMATION && isValidPhone(values.phone_number)) {
      onPhoneVerificationRequest(values);
    }

    if (step === STEPS.PIN_VERIFICATION) {
      onPinVerify(values);
    }

    if (step === STEPS.EMAIL_REGISTRATION) {
      onEmailRegistrationRequest(values);
    }

    if (step === STEPS.EMAIL_VERIFICATION) {
      onEmailVerificationRequest(values);
    }
  };

  const onValidate = values => {
    const errors = {};

    switch (step) {
      case STEPS.USER_INFORMATION: {
        if (
          values.phone_number.replace('+1', '').length > 0 &&
          !isValidPhone(values.phone_number)
        ) {
          errors.phone_number = 'Phone number is not valid';
        }
        break;
      }
      case STEPS.EMAIL_REGISTRATION: {
        if (values.password.length < 8) {
          errors.password = 'Password must be longer than 8 characters.';
        }
        if (values.password !== values.password_verification) {
          errors.password_verification = 'Passwords must match';
        }
        break;
      }
      default:
        break;
    }
    return errors;
  };

  if (checkingEmailVerificationStepRequirements || !marketingAccountId) {
    return <FullScreenLoader />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Waldo &ndash; Register</title>
      </Helmet>
      <SignupAlbumLayout.Wrapper withCTA={false}>
        <Formik
          onSubmit={onSubmit}
          initialValues={{
            first_name: '',
            last_name: '',
            email_address: showEmailVerificationStep ? emailAddress ?? '' : '',
            should_verify_email_address: true,
            phone_number: '+1',
            password: '',
            password_verification: '',
            code: '',
            force_allow_action: true,
          }}
          validate={onValidate}
          validateOnBlur={false}
          validateOnChange={false}
          render={({
            values,
            handleSubmit,
            handleChange,
            handleBlur,
            errors,
            touched,
          }) => {
            return (
              <div className="shutterbug-registration-form-wrapper">
                {step === STEPS.USER_INFORMATION && (
                  <UserInformationStep
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    errors={errors}
                    touched={touched}
                    values={values}
                    handleSubmit={handleSubmit}
                    identityName={`${firstName ?? ''} ${lastName ?? ''}`.trim()}
                    onUseEmailRegistration={() =>
                      setStep(STEPS.EMAIL_REGISTRATION)
                    }
                    requireHumanInput={requireHumanInput}
                    blockLogin={blockLogin}
                  />
                )}
                {step === STEPS.PIN_VERIFICATION && (
                  <PinVerificationStep
                    loading={loading}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    errors={errors}
                    touched={touched}
                    values={values}
                    handleSubmit={handleSubmit}
                    onPinCodeResend={onPinCodeResend}
                    requireHumanInput={requireHumanInput}
                    blockLogin={blockLogin}
                  />
                )}
                {step === STEPS.EMAIL_REGISTRATION && (
                  <EmailRegistrationStep
                    loading={loading}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    errors={errors}
                    touched={touched}
                    values={values}
                    identityName={`${firstName ?? ''} ${lastName ?? ''}`.trim()}
                    handleSubmit={handleSubmit}
                  />
                )}
                {step === STEPS.EMAIL_VERIFICATION && (
                  <EmailVerificationStep
                    loading={loading}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    errors={errors}
                    touched={touched}
                    values={values}
                    handleSubmit={handleSubmit}
                  />
                )}
                {step === STEPS.EMAIL_VERIFICATION_SENT && (
                  <EmailVerificationSentStep values={values} />
                )}
              </div>
            );
          }}
        />
      </SignupAlbumLayout.Wrapper>
    </Fragment>
  );
};

export default compose(withGoogleRecaptchaProvider)(
  MarketingAccountMatchesView,
);
